import { SyncCoreDataDefinitionsEnumTranslator, TaskStatus } from '@edgebox/sync-core-data-definitions';

/**
 * What the "continue an existing onboarding" control is allowed to say.
 *
 * The control names the domain being resumed, and naming it is only possible when the domain is
 * actually known. It was previously given the string `'Unknown domain'` as a fallback, which put
 * "Continue with Unknown domain" on a button — a label that reads as a fact about the customer's
 * project and is not one. A placeholder is worse here than saying less: a reader cannot tell
 * whether their domain was lost, misdetected, or simply not loaded yet.
 *
 * So there is no fallback string. Either the label names a real domain, or it does not claim one.
 */

/** An onboarding a customer can resume. `domain` is absent until it is genuinely known. */
export interface ResumableOnboarding {
  id: string;
  domain?: string;
}

/** The label when the domain is not known — names the action without asserting a subject. */
export const CONTINUE_WITHOUT_DOMAIN_LABEL = 'Continue setup';

/** What to show while the lookup is still in flight. */
export const CONTINUE_LOADING_LABEL = 'Loading…';

/** What a cell of the list says of a fact it cannot name. */
const EM_DASH = '—';

/**
 * The button label for resuming an onboarding.
 *
 * `loading` wins over everything: during the lookup the honest statement is that we do not know
 * yet, and a control that renders its final wording early makes a loading state indistinguishable
 * from a finished one.
 */
export function onboardingContinueLabel(taskGroup: ResumableOnboarding | undefined, loading: boolean): string {
  if (loading) {
    return CONTINUE_LOADING_LABEL;
  }

  const domain = taskGroup?.domain?.trim();
  return domain ? `Continue with ${domain}` : CONTINUE_WITHOUT_DOMAIN_LABEL;
}

/**
 * How a resumable onboarding's domain reads in a list.
 *
 * Same rule, different shape: an em dash states the absence rather than inventing a name for it.
 */
export function onboardingDomainCell(taskGroup: ResumableOnboarding): string {
  return taskGroup.domain?.trim() || EM_DASH;
}

/** Where a resumable onboarding stands, in the words the package gives its status. */
export function onboardingStatusCell(status: TaskStatus | undefined): string {
  return (status && SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaskStatus', status)) || EM_DASH;
}
